// src/organization/useOrganizationRole.ts
import { API_ENDPOINTS } from '@/api/endpoints';
import { apiFetch } from '@/util/apiClient';
import { useQuery } from '@tanstack/react-query';

interface OrganizationRole {
  isMember: boolean;
  isAdmin: boolean;
}

export function useOrganizationRole(orgId?: string) {
  return useQuery({
    queryKey: ['organization', orgId, 'role'],
    queryFn: async (): Promise<OrganizationRole> => {
      if (!orgId) {
        return { isMember: false, isAdmin: false };
      }
      return apiFetch<OrganizationRole>(
        API_ENDPOINTS.ORGANIZATIONS.ROLE(orgId),
      );
    },
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export interface OrganizationPerms {
  canEditOrgSettings: boolean;
  canManageUsers: boolean;
  canManageFiscalYrs: boolean;
  canManageDepts: boolean;
  canManageProjs: boolean;
  canApproveDesign: boolean;
  canApproveBudget: boolean;
}

export function useOrganizationPerms(orgId?: string) {
  return useQuery({
    queryKey: ['organization', orgId, 'perms'],
    queryFn: async (): Promise<OrganizationPerms> => {
      if (!orgId) {
        return {
          canEditOrgSettings: false,
          canManageUsers: false,
          canManageFiscalYrs: false,
          canManageDepts: false,
          canManageProjs: false,
          canApproveDesign: false,
          canApproveBudget: false,
        };
      }
      return apiFetch<OrganizationPerms>(
        API_ENDPOINTS.ORGANIZATIONS.PERMS(orgId),
      );
    },
    enabled: Boolean(orgId),
    staleTime: 0, // Always refetch to get latest permissions
    refetchOnWindowFocus: true, // Refetch when user comes back to the tab
    refetchOnMount: true, // Refetch when component mounts
  });
}
